import React, { Fragment } from "react";
import { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router";
import { Button } from "react-bootstrap";
import AdminService from "../../services/adminService";
import { encodeQueryData } from "../../util/queryUtil";
import shift from "../deficit/employeeDeficit";
import { IOption, IShift, IShiftIdDTO } from "./interface";
import checkMark from "../../resource/icons/green_check_mark.png";
import crossMark from "../../resource/icons/red_cross_mark.png";
import "./shift.css";
import NotificationModal from "../notificationModal";
import Select, { Props, SingleValue } from "react-select";
import { AxiosResponse } from "axios";
import Spinner from "../spinner";
import DeleteModal from "./deleteModal";
import { useDispatch, useSelector } from 'react-redux';
import { setGlobalAlert } from '../../state/action-creators/alert';
import { State } from "../../state";

export default function Shift() {
  const listAmountOptions: any[] = [
    { value: 20, label: 20 },
    { value: 50, label: 50 },
    { value: 60, label: 60 },
    { value: 70, label: 70 },
    { value: 80, label: 80 },
    { value: 90, label: 90 },
    { value: 100, label: 100 },
    { value: 150, label: 150 },
    { value: 200, label: 200 },
    { value: 250, label: 250 },
  ];
  const [isShiftCompleted, setIsShiftCompleted] = useState(false);
  const [isShiftLocked, setIsShiftLocked] = useState(false);
  const [isDeposited, setIsDeposited] = useState(false);
  const [isDepositVerified, setIsDepositVerified] = useState(false);
  const [isCreditCardVerified, setIsCreditCardVerified] = useState(false);
  const [isDeficitDeducted, setIsDeficitDeducted] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(true);
  const [shifts, setShifts] = useState<IShift[]>();
  const [listAmount, setListAmount] = useState<SingleValue<IOption>>(
    listAmountOptions[0]
  );
  const [sortBy, setSortBy] = useState("desc");
  const [isShiftEntityIdSearchActive, setIsShiftEntityIdSearchActive] =
    useState(false);
  const [firstShift, setFirstShift] = useState<SingleValue<IOption>>();
  const [lastShift, setLastShift] = useState<SingleValue<IOption>>();
  const [firstShiftSelectOptions, setFirstShiftSelectOptions] =
    useState<any[]>();
  const [lastShiftSelectOptions, setLastShiftSelectOptions] = useState<any[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchFailed, setIsFetchFailed] = useState(false);
  const [isDeleteModeOn, setIsDeleteModeOn] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteShift, setDeleteShift] = useState<IShift>();
  const history = useHistory();
  const dispatch = useDispatch();
  const languageData = useSelector((state: State) => state.languageReducer);

  useEffect(() => {
    getShiftId();
  }, []);

  const id = useLocation().pathname.split("/")[3];
  // console.log(useLocation().pathname.split("/")[3]);

  useEffect(() => {
    if (id) {
      getShiftFromPathVariable(id);
    }
  }, [id]);

  const getShiftFromPathVariable = async (id: string) => {
    try {
      const response = await AdminService.getShiftById(id);
      setIsLoading(false);
      const updatedShifts = response.data.map((data: IShift) => {
        return {
          ...data,
          isLockedLoading: false,
          isLockedPatchFailed: false,
          isLockedSaved: true,
          isCompletedLoading: false,
          isCompletedPatchFailed: false,
          isCompletedSaved: true,
          isDeleteFailed: false,
        };
      });
      setShifts(updatedShifts);
    } catch (err: any) {
      dispatch(setGlobalAlert(
        err.response?.data?.message || "Failed to load shift details",
        'error',
        err.response?.data?.details
      ));
      setIsLoading(false);
      setIsFetchFailed(true);
    }
  };

  const getShiftId = async () => {
    try {
      const response = await AdminService.getShiftId();
      const selectOptions = response.data.map((dto: IShiftIdDTO) => {
        return { value: dto.shiftsEntityId, label: dto.shiftId };
      });
      setFirstShiftSelectOptions(selectOptions);
      setLastShiftSelectOptions(selectOptions);
    } catch (err: any) {
      console.dir(err);
      dispatch(setGlobalAlert(
          err.response?.data?.message || "Failed to load shift details",
          'error',
          err.response?.data?.details
      ));
    }
  };

  const handleToggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const handleDeleteShiftButtonClicked = (shift: IShift) => {
    setDeleteShift(shift);
    handleToggleDeleteModal();
  };

  const handleChangeIsDeleteModeOn = () => {
    setIsDeleteModeOn(!isDeleteModeOn);
  };

  const handleDeleteShift = async (isHardDelete: boolean) => {
    handleToggleDeleteModal();
    try {
      if (deleteShift) {
        const response = await AdminService.deleteShift(
          deleteShift.shiftsEntityId,
          isHardDelete
        );
        if (response.status === 204 && shifts) {
          const updatedShifts = shifts.filter((shift: IShift) => {
            if (shift.shiftsEntityId === deleteShift.shiftsEntityId) {
              return false;
            }
            return true;
          });
          setShifts(updatedShifts);
        }
      }
    } catch (err: any) {
      const updatedShifts = shifts?.map((shift: IShift) => {
        if (shift.shiftsEntityId === deleteShift?.shiftsEntityId) {
          shift.isDeleteFailed = true;
          if (err.response.data.message !== "undefined") {
            shift.deleteFailMessage = err.response.data.message;
          }
          return shift;
        }
        return shift;
      });
      setShifts(updatedShifts);
      if (typeof err.response.data.message !== "undefined") {
        dispatch(setGlobalAlert(
          err.response.data.message,
          'error',
          err.response.data.details
        ));
      }
    }
  };

  const handleChangeIsShiftCompleted = () => {
    setIsShiftCompleted(!isShiftCompleted);
  };

  const handleChangeIsShiftLocked = () => {
    setIsShiftLocked(!isShiftLocked);
  };

  const handleChangeIsDepositVerified = () => {
    setIsDepositVerified(!isDepositVerified);
  };

  const handleChangeIsCreditCardVerified = () => {
    setIsCreditCardVerified(!isCreditCardVerified);
  };

  const handleChangeIsDeficitDeducted = () => {
    setIsDeficitDeducted(!isDeficitDeducted);
  };

  const handleChangeIsDeposited = () => {
    setIsDeposited(!isDeposited);
  };

  const handleToggleIsFilterActive = () => {
    setIsFilterActive(!isFilterActive);
  };
  const handleListAmountChange = (option: SingleValue<IOption>) => {
    setListAmount(option);
  };
  const handleSortByChange = (event: any) => {
    setSortBy(event.target.value);
  };

  const handleSearchButtonClick = async () => {
    window.history.pushState("", "", "/admin/shift");
    setIsLoading(true);
    setIsFetchFailed(false);
    let queryParams = "";
    if (!isFilterActive) {
      const data = {
        iscompleted: isShiftCompleted,
        islocked: isShiftLocked,
        isdepositverified: isDepositVerified,
        isdeficitdeducted: isDeficitDeducted,
        iscreditcardverified: isCreditCardVerified,
      };
      queryParams = encodeQueryData(data);
    }
    try {
      if (isShiftEntityIdSearchActive) {
        queryParams +=
          "&" +
          encodeQueryData({
            firstshiftsentityid: firstShift?.value,
            lastshiftsentityid: lastShift?.value,
          });
      }
      queryParams += "&" + encodeQueryData({ limit: listAmount?.value });
      queryParams += "&" + encodeQueryData({ sort_by: sortBy });
      const response = await AdminService.getShift(queryParams);
      setIsLoading(false);
      console.log(response.data);
      const updatedShifts = response.data.map((data: IShift) => {
        return {
          ...data,
          isLockedLoading: false,
          isLockedPatchFailed: false,
          isLockedSaved: true,
          isCompletedLoading: false,
          isCompletedPatchFailed: false,
          isCompletedSaved: true,
          isDeleteFailed: false,
        };
      });
      setShifts(updatedShifts);
    } catch (err) {
      setIsLoading(false);
      setIsFetchFailed(true);
      console.log(err);
    }
  };

  const handleChangeFirstShift = (option: SingleValue<IOption>) => {
    console.log(option?.value);
    setFirstShift(option);
    if (option == null) {
      setLastShift(option);
    }
    if (
      lastShift == null ||
      (option != null && option?.value > lastShift.value)
    ) {
      setLastShift(option);
      const updatedSelectOptions = firstShiftSelectOptions?.filter(
        (opt: IOption) => {
          if (option && opt.value >= option?.value) {
            return true;
          }
          return false;
        }
      );
      setLastShiftSelectOptions(updatedSelectOptions);
    }
  };

  const handleChangeLastShift = (option: SingleValue<IOption>) => {
    console.log(option?.value);
    setLastShift(option);
  };

  const handleChangeIsShiftEntityIdSearchActive = () => {
    setIsShiftEntityIdSearchActive(!isShiftEntityIdSearchActive);
  };

  const handleChangeEntityIsShiftCompleted = async (
    e: any,
    shiftsEntityId: number
  ) => {
    const isCompleted = e.target.value === "true";
    try {
      const updatedShifts = shifts?.map((shift: IShift) => {
        if (shiftsEntityId === shift.shiftsEntityId) {
          shift.shiftCompleted = isCompleted;
          shift.isCompletedLoading = true;
          shift.isCompletedSaved = false;
          shift.isCompletedPatchFailed = false;
          return shift;
        }
        return shift;
      });
      setShifts(updatedShifts);

      const response = await AdminService.patchShift(
        { completed: isCompleted },
        shiftsEntityId
      );
      if (response.status === 204) {
        const updatedShifts = shifts?.map((shift: IShift) => {
          if (shiftsEntityId === shift.shiftsEntityId) {
            shift.isCompletedLoading = false;
            shift.isCompletedSaved = true;
            shift.isCompletedPatchFailed = false;
            return shift;
          }
          return shift;
        });
        setShifts(updatedShifts);
        dispatch(setGlobalAlert("Shift completed status updated successfully", 'success'));
      }
    } catch (err: any) {
      console.dir(err);
      if (typeof err.response.data.message !== "undefined") {
        dispatch(setGlobalAlert(
          err.response.data.message,
          'error',
          err.response.data.details
        ));
      }
      const updatedShifts = shifts?.map((shift: IShift) => {
        if (shiftsEntityId === shift.shiftsEntityId) {
          shift.isCompletedLoading = false;
          shift.isCompletedSaved = false;
          shift.isCompletedPatchFailed = true;
          shift.shiftCompleted = !isCompleted;
          return shift;
        }
        return shift;
      });
      setShifts(updatedShifts);
    }
  };

  const handleChangeEntityIsShiftLocked = async (
    e: any,
    shiftsEntityId: number
  ) => {
    const isLocked = e.target.value === "true";
    try {
      const updatedShifts = shifts?.map((shift: IShift) => {
        if (shiftsEntityId === shift.shiftsEntityId) {
          shift.shiftLocked = isLocked;
          shift.isLockedLoading = true;
          shift.isLockedSaved = false;
          shift.isLockedPatchFailed = false;
          return shift;
        }
        return shift;
      });
      setShifts(updatedShifts);

      const response = await AdminService.patchShift(
        { locked: isLocked },
        shiftsEntityId
      );
      if (response.status === 204) {
        const updatedShifts = shifts?.map((shift: IShift) => {
          if (shiftsEntityId === shift.shiftsEntityId) {
            shift.isLockedLoading = false;
            shift.isLockedSaved = true;
            shift.isLockedPatchFailed = false;
            return shift;
          }
          return shift;
        });
        setShifts(updatedShifts);
        dispatch(setGlobalAlert("Shift lock status updated successfully", 'success'));
      }
    } catch (err: any) {
      console.dir(err);
      if (typeof err.response.data.message !== "undefined") {
        dispatch(setGlobalAlert(
          err.response.data.message,
          'error',
          err.response.data.details
        ));
      }
      
      const updatedShifts = shifts?.map((shift: IShift) => {
        if (shiftsEntityId === shift.shiftsEntityId) {
          shift.isLockedLoading = false;
          shift.isLockedSaved = false;
          shift.isLockedPatchFailed = true;
          shift.shiftLocked = !isLocked;
          return shift;
        }
        return shift;
      });
      setShifts(updatedShifts);
    }
  };

  return (
    <div>
      <div className="shift-search-main justify-content-center">
        <div className="shift-other-search justify-content-center">
          <div className="shift-search-checkbox">
            <label>Filter Active</label>
            <input
              key="isFilterActive"
              type="checkbox"
              value="isFilterActive"
              name="isFilterActive"
              onChange={handleToggleIsFilterActive}
              checked={isFilterActive}
            />
          </div>
          <div className="shift-search-group justify-content-center">
            <div className="shift-search-checkbox">
              <label>Shift Completed</label>
              <input
                key="isShiftCompleted"
                disabled={!isFilterActive}
                type="checkbox"
                value="isShiftCompleted"
                name="isShiftCompleted"
                onChange={handleChangeIsShiftCompleted}
                checked={isShiftCompleted}
              />
            </div>
            <div className="shift-search-checkbox">
              <label>Shift Locked</label>
              <input
                key="isShiftLocked"
                disabled={!isFilterActive}
                type="checkbox"
                value="isShiftLocked"
                name="isShiftLocked"
                onChange={handleChangeIsShiftLocked}
                checked={isShiftLocked}
              />
            </div>
            <div className="shift-search-checkbox">
              <label>Cash Deposited</label>
              <input
                key="isDeposited"
                disabled={!isFilterActive}
                type="checkbox"
                value="isDeposited"
                name="isDeposited"
                onChange={handleChangeIsDeposited}
                checked={isDeposited}
              />
            </div>
            <div className="shift-search-checkbox">
              <label>Deposit Verified</label>
              <input
                key="isDepositVerified"
                disabled={!isFilterActive}
                type="checkbox"
                value="isDepositVerified"
                name="isDepositVerified"
                onChange={handleChangeIsDepositVerified}
                checked={isDepositVerified}
              />
            </div>
            <div className="shift-search-checkbox">
              <label>Credit Card Verified</label>
              <input
                key="isCreditCardVerified"
                disabled={!isFilterActive}
                type="checkbox"
                value="isCreditCardVerified"
                name="isCreditCardVerified"
                onChange={handleChangeIsCreditCardVerified}
                checked={isCreditCardVerified}
              />
            </div>
            <div className="shift-search-checkbox">
              <label>Deficit Deducted</label>
              <input
                key="isDeficitDeducted"
                disabled={!isFilterActive}
                type="checkbox"
                value="isDeficitDeducted"
                name="isDeficitDeducted"
                onChange={handleChangeIsDeficitDeducted}
                checked={isDeficitDeducted}
              />
            </div>
          </div>

          <hr />
          {firstShiftSelectOptions && firstShiftSelectOptions.length > 0 && (
            <div className="search-by-id-section">
              <div className="shift-search-checkbox">
                <label>Search By Id Filter</label>
                <input
                  key="isShiftEntityIdSearchActive"
                  type="checkbox"
                  value="isShiftEntityIdSearchActive"
                  name="isShiftEntityIdSearchActive"
                  onChange={handleChangeIsShiftEntityIdSearchActive}
                  checked={isShiftEntityIdSearchActive}
                />
              </div>

              <div className="shift-dropdown-container">
                <div className="shift-shift-dropdown">
                  <label>First Shift</label>
                  <div className="shift-react-dropdown">
                    <Select
                      options={firstShiftSelectOptions}
                      value={firstShift}
                      onChange={handleChangeFirstShift}
                      isSearchable={true}
                      isClearable={true}
                      placeholder="Enter Shift Id"
                      isDisabled={!isShiftEntityIdSearchActive}
                    />
                  </div>
                </div>

                <div className="shift-shift-dropdown">
                  <label>Last Shift</label>
                  <div className="shift-react-dropdown">
                    <Select
                      options={lastShiftSelectOptions}
                      value={lastShift}
                      onChange={handleChangeLastShift}
                      isSearchable={true}
                      isClearable={true}
                      placeholder="Enter Shift Id"
                      isDisabled={!isShiftEntityIdSearchActive}
                    />
                  </div>
                </div>
              </div>
              <hr />
            </div>
          )}

          <div className="shift-radio-main-div">
            <div className="shift-radio-div">
              <label>Ascending</label>
              <input
                type="radio"
                value="asc"
                onChange={handleSortByChange}
                checked={sortBy === "asc"}
              />
            </div>
            <div className="shift-radio-div">
              <label>Descending</label>
              <input
                type="radio"
                value="desc"
                onChange={handleSortByChange}
                checked={sortBy === "desc"}
              />
            </div>
          </div>
          <hr />
          {
            <div className="shift-dropdown">
              <label>List Amount</label>
              <Select
                options={listAmountOptions}
                value={listAmount}
                onChange={handleListAmountChange}
                isSearchable={false}
              />
              {/* <select defaultValue="20" onChange={handleListAmountChange}>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select> */}
            </div>
          }
          <hr />
          <Button onClick={handleSearchButtonClick}>
            {languageData?.data?.admin?.shift?.filters?.search}
          </Button>
          <hr />
        </div>
      </div>

      {isLoading === false &&
      isFetchFailed === false &&
      shifts !== undefined &&
      shifts?.length === 0 ? (
        <h3>Couldn't find shift</h3>
      ) : null}
      {isLoading === true && isFetchFailed === false && <Spinner />}
      {isLoading === false && isFetchFailed === true && <h3>Fetch Failed</h3>}
      {isLoading === false &&
        isFetchFailed === false &&
        shifts &&
        shifts.length > 0 && (
          <div className="justify-content-center">
            <div
              className="shift-search-checkbox"
              style={{ margin: "auto", display: "block" }}
            >
              <label>Delete Mode</label>
              <input
                key="isDeleteModeOn"
                type="checkbox"
                value="isDeleteModeOn"
                name="isDeleteModeOn"
                onChange={handleChangeIsDeleteModeOn}
                checked={isDeleteModeOn}
              />
            </div>

            <div className="shift-table-main-div" key="shifts">
              <table>
                <thead>
                  <tr>
                    {isDeleteModeOn === true && (
                      <th className="shift-custom-th-td">Delete</th>
                    )}
                    <th className="shift-custom-th-td">
                      {languageData?.data?.admin?.shift?.table?.shiftId}
                    </th>
                    <th className="shift-custom-th-td">
                      {languageData?.data?.admin?.shift?.table?.completed}
                    </th>
                    <th className="shift-custom-th-td">Shift Locked</th>
                    <th className="shift-custom-th-td">Cash Deposited</th>
                    <th className="shift-custom-th-td">Deposit Verified</th>
                    <th className="shift-custom-th-td">Credit Card Verified</th>
                    <th className="shift-custom-th-td">Deficit Deducted</th>
                    <th className="shift-custom-th-td">Deficit</th>
                    <th className="shift-custom-th-td">Total Employee Sale</th>
                    <th className="shift-custom-th-td">Cash</th>
                    <th className="shift-custom-th-td">Credit Card</th>
                    <th className="shift-custom-th-td">Partner Card</th>
                    <th className="shift-custom-th-td">Gift Card</th>
                    <th className="shift-custom-th-td">Mobile</th>
                    <th className="shift-custom-th-td">Distributor Sales</th>
                    <th className="shift-custom-th-td">Company Sales</th>
                    <th className="shift-custom-th-td">Test Sales</th>
                    <th className="shift-custom-th-td">Total Sale</th>
                  </tr>
                </thead>
                <tbody>
                  {shifts.map((shift: IShift) => {
                    let shiftCompletedSelectClass = "";
                    if (shift.isCompletedLoading) {
                      shiftCompletedSelectClass += " shift-loading";
                    }
                    if (shift.isCompletedPatchFailed) {
                      shiftCompletedSelectClass += " shift-failed";
                    }
                    if (shift.isCompletedSaved) {
                      shiftCompletedSelectClass += " shift-saved";
                    }
                    let shiftLockedSelectClass = "";
                    if (shift.isLockedLoading) {
                      shiftLockedSelectClass += " shift-loading";
                    }
                    if (shift.isLockedPatchFailed) {
                      shiftLockedSelectClass += " shift-failed";
                    }
                    if (shift.isLockedSaved) {
                      shiftLockedSelectClass += " shift-saved";
                    }
                    return (
                      <Fragment key={shift.shiftsEntityId}>
                        <tr key={shift.shiftsEntityId}>
                          {isDeleteModeOn === true && (
                            <td
                              title={
                                shift.isDeleteFailed
                                  ? shift.deleteFailMessage
                                  : "Delete Shift"
                              }
                              className={
                                "shift-custom-th-td" +
                                (shift.isDeleteFailed === true ? " failed" : "")
                              }
                            >
                              <Button
                                style={{ width: "100%", margin: "1%" }}
                                onClick={() =>
                                  handleDeleteShiftButtonClicked(shift)
                                }
                              >
                                Delete
                              </Button>{" "}
                            </td>
                          )}

                          <td className={"shift-custom-th-td"}>
                            {shift.shiftId}
                          </td>

                          <td
                            className={
                              "shift-custom-th-td " + shiftCompletedSelectClass
                            }
                          >
                            <select
                              disabled={shift.isCompletedLoading}
                              value={`${shift.shiftCompleted}`}
                              onChange={(e) =>
                                handleChangeEntityIsShiftCompleted(
                                  e,
                                  shift.shiftsEntityId
                                )
                              }
                            >
                              <option value="true">✔</option>
                              <option value="false">❌</option>
                            </select>
                          </td>
                          <td
                            className={
                              "shift-custom-th-td " + shiftLockedSelectClass
                            }
                          >
                            <select
                              disabled={shift.isLockedLoading}
                              value={`${shift.shiftLocked}`}
                              onChange={(e) =>
                                handleChangeEntityIsShiftLocked(
                                  e,
                                  shift.shiftsEntityId
                                )
                              }
                            >
                              <option value="true">✔</option>
                              <option value="false">❌</option>
                            </select>
                          </td>
                          <td className="shift-custom-th-td">
                            {
                              shift.deposited ? (
                                <label className="boolean-emoji">✔</label>
                              ) : (
                                <label className="boolean-emoji">❌</label>
                              )
                            }
                          </td>
                          <td
                            title={"Click to go deposit of the shift."}
                            className="shift-custom-th-td"
                            onClick={() =>
                              history.push(
                                "/admin/deposit?firstshiftsentityid=" +
                                  shift.shiftsEntityId +
                                  "&lastshiftsentityid=" +
                                  shift.shiftsEntityId
                              )
                            }
                          >
                            {
                              shift.depositVerified ? (
                                <label className="boolean-emoji">✔</label>
                              ) : (
                                <label className="boolean-emoji">❌</label>
                              )
                            }
                          </td>
                          <td
                            title={"Click to go credit card of the shift."}
                            className="shift-custom-th-td"
                            onClick={() =>
                              history.push(
                                "/admin/creditcard?firstshiftsentityid=" +
                                  shift.shiftsEntityId +
                                  "&lastshiftsentityid=" +
                                  shift.shiftsEntityId
                              )
                            }
                          >
                            {
                              shift.creditCardVerified ? (
                                <label className="boolean-emoji">✔</label>
                              ) : (
                                <label className="boolean-emoji">❌</label>
                              )
                            }
                          </td>
                          <td
                            title={"Click to go deficit of the shift."}
                            className="shift-custom-th-td"
                            onClick={() =>
                              history.push(
                                "/admin/deficit?firstshiftsentityid=" +
                                  shift.shiftsEntityId +
                                  "&lastshiftsentityid=" +
                                  shift.shiftsEntityId
                              )
                            }
                          >
                            {
                              shift.deficitDeducted ? (
                                <label className="boolean-emoji">✔</label>
                              ) : (
                                <label className="boolean-emoji">❌</label>
                              )
                            }
                          </td>
                          <td className="shift-custom-th-td">
                            {shift.deficit}
                          </td>
                          <td className="shift-custom-th-td">
                            {shift.totalEmployeeSale}
                          </td>
                          <td className="shift-custom-th-td">{shift.cash}</td>
                          <td className="shift-custom-th-td">
                            {shift.creditCard}
                          </td>
                          <td className="shift-custom-th-td">
                            {shift.partnerCard}
                          </td>
                          <td className="shift-custom-th-td">
                            {shift.giftCard}
                          </td>
                          <td className="shift-custom-th-td">{shift.mobile}</td>
                          <td className="shift-custom-th-td">
                            {shift.distributorSales}
                          </td>
                          <td className="shift-custom-th-td">
                            {shift.companySales}
                          </td>
                          <td className="shift-custom-th-td">{shift.test}</td>
                          <td className="shift-custom-th-td">
                            {shift.totalSale}
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <DeleteModal
              isHardDeleteActive={true}
              buttonText="Delete"
              onTrue={handleDeleteShift}
              handleClose={handleToggleDeleteModal}
              show={showDeleteModal}
              question={`Would you like to delete shift ${deleteShift?.shiftId}?`}
            />
          </div>
        )}
    </div>
  );
}
